import React, { useState, useMemo } from 'react';
import { alpha, Box, Card, CardContent, Chip, IconButton, Stack, Typography } from '@mui/material';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import { useDirtyTrackingStore } from '../../stores/dirtyTrackingStore';
import { useAuthStore } from '../../stores/authStore';
import { treasureEffect } from '../../App';
import { hashStringToSeed, generateBloodSpatterSVG, createSubtleIceSurface } from '../../theme/surfaces';
import { EditNameDialog } from './EditNameDialog';
import { WarbandMemberCardModal } from '../modals/WarbandMemberCardModal';
import { MemberImage } from './MemberImage';
import { CurrentHealthUpdateControls } from './CurrentHealthUpdateControls';
import { WarbandMemberCardProps } from './WarbandMemberCard';
import FileUploadIcon from '@mui/icons-material/FileUpload';

export const WarbandMemberMiniCard = React.memo(function WarbandMemberMiniCard({
  member,
  title = member.name,
  isDefeated = false,
  titleActionContent,
  titleMenuItems = [],
  bottomRightActionContent,
  showArmourWhenEmpty,
  onItemClick,
  editMode = false,
  unusedLevelUps,
  onApplyLevelUp,
  levelUpBonuses,
}: WarbandMemberCardProps) {
  const { warbandId, id: memberId } = member;

  const memberRole: 'wizard' | 'apprentice' | 'soldier' =
    member.creatureId?.toLowerCase() === 'wizard' ? 'wizard'
      : member.creatureId?.toLowerCase() === 'apprentice' ? 'apprentice'
        : 'soldier';

  const updateMemberName = useDirtyTrackingStore((s) => s.updateMemberName);
  const updateMemberHealth = useDirtyTrackingStore((s) => s.updateMemberHealth);
  const accessToken = useAuthStore((s) => s.accessToken);
  const [isEditNameOpen, setIsEditNameOpen] = useState(false);
  const [isCardModalOpen, setIsCardModalOpen] = useState(false);

  const bloodSpatterUrl = useMemo(() => generateBloodSpatterSVG(hashStringToSeed(memberId)), [memberId]);
  const frostedGlassCardSx = useMemo(() => createSubtleIceSurface(hashStringToSeed(memberId), {}), [memberId]);

  return (
    <Card sx={{ ...frostedGlassCardSx, position: 'relative', width: '280px' }} elevation={3}>
      {isDefeated && (
        <Box
          aria-hidden
          style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 2 }}
          sx={{
            borderRadius: 'inherit',
            pointerEvents: 'none',
            backgroundImage: bloodSpatterUrl,
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
      )}
      <CardContent style={{ position: 'relative', zIndex: 3 }}>

        {/* Main content, fills remaining space */}
        <Box sx={{ flex: 1 }}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1} sx={{ width: '100%' }}>
            <MemberImage
              warbandId={warbandId}
              memberId={memberId}
              editMode={editMode}
              minified
              isTemporary={member.isTemporary}
              accessToken={accessToken}
            />

            <Stack direction="column" alignItems="flex-start" justifyContent="space-between" spacing={1} sx={{ width: '100%' }}>
              <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1} sx={{ width: '100%' }}>
                <Typography variant="subtitle2" textAlign={'left'} sx={{ fontWeight: 700 }}>
                  {title}
                </Typography>
                {member.carryingTreasure && (
                  <Chip
                    size='small'
                    label={"-"}
                    sx={{ ...treasureEffect, color: '#EDD270', cursor: 'pointer' }}
                  />
                )}

                {editMode && (unusedLevelUps ?? 0) > 0 && (
                  <FileUploadIcon fontSize="small"

                    sx={{
                      backgroundColor: alpha('#FFD700', 0.55),

                    }}
                  />
                )}

                <IconButton
                  size="small"
                  onClick={() => setIsCardModalOpen(true)}
                  aria-label="View full member card"
                  sx={{ ml: 'auto', flexShrink: 0 }}
                >
                  <OpenInFullIcon fontSize="inherit" />
                </IconButton>

              </Stack>

              <Box >
                <CurrentHealthUpdateControls
                  member={member}
                  saving={false}
                  healthBonus={levelUpBonuses?.health ?? 0}
                  onSave={(nextHealth) => updateMemberHealth(memberId, memberRole, nextHealth)}
                />
              </Box>
            </Stack>
          </Stack>
        </Box>
      </CardContent>

      <EditNameDialog
        open={isEditNameOpen}
        label="Name"
        initialValue={member.name}
        onClose={() => setIsEditNameOpen(false)}
        onSave={(newName) => {
          updateMemberName(memberId, memberRole, newName);
          setIsEditNameOpen(false);
        }}
      />

      <WarbandMemberCardModal
        open={isCardModalOpen}
        onClose={() => setIsCardModalOpen(false)}
        memberCardProps={{
          editMode: editMode,
          member: member,
          onItemClick: onItemClick,
          isDefeated: isDefeated,
          levelUpBonuses: levelUpBonuses,
          titleMenuItems: titleMenuItems,
          unusedLevelUps: unusedLevelUps,
          onApplyLevelUp: onApplyLevelUp,
          titleActionContent: titleActionContent,
          bottomRightActionContent: bottomRightActionContent,
        }}
      />
    </Card>
  );
});